// Global difficulty profiles. Three tiers exposed in the menu.
// Each profile is a bag of multipliers that flow into:
//   - EndlessGenerator: speed floor/ceiling, risk/score probability,
//     warmup gating, boss cadence
//   - MorphHexGame: story scroll multiplier, last-chance window, score
//     reward multiplier, best-score key suffix
//
// Choose by id; undefined ids fall back to "normal" so saved selections
// from a future schema bump don't crash the loader.

export type DifficultyId = "easy" | "normal" | "hard";

export interface DifficultyProfile {
  id: DifficultyId;
  label: string;
  // Whole-game speed scale. EndlessGenerator multiplies its speed
  // floor/ceiling by this; MorphHexGame multiplies the story dt
  // streamer-scroll by this so handcrafted levels also feel the
  // difficulty.
  speedMul: number;
  // Adds to RISK_WARMUP_CHUNKS (positive = later, negative = earlier).
  // Easy delays risk pickups so new players aren't punished for not
  // knowing what shield/slowmo orbs do; Hard front-loads them so the
  // player has tools but also temptation.
  riskWarmupBonus: number;
  // Multiplies RISK_PROB (shield + slowmo orb spawn chance).
  riskProbMul: number;
  // Multiplies SCORE_PROB. Easy showers safe orbs; Hard rations them.
  scoreProbMul: number;
  // Multiplies TRICK_PROB sprinkle in EndlessGenerator (mirror / pulse
  // / sweep). Hard bumps it; Easy halves it.
  trickProbMul: number;
  // Multiplies the boss cadence (BOSS_EVERY). 0.8 = bosses ~25% more
  // frequent (rounds down to a smaller integer).
  bossEveryMul: number;
  // Multiplies the player's LAST_CHANCE_WINDOW_S. Wider = more
  // forgiving micro-rotation save window before a graze becomes a
  // crash.
  lastChanceMul: number;
  // Multiplies every score gain via ScoreSystem.setRewardMultiplier
  // so harder runs ARE rewarded for being harder. Composes with the
  // double-pickup window in PickupManager.
  scoreMul: number;
}

export const DIFFICULTY_PROFILES: Record<DifficultyId, DifficultyProfile> = {
  easy: {
    id: "easy",
    label: "EASY",
    speedMul: 0.85,
    riskWarmupBonus: 4,
    riskProbMul: 0.7,
    scoreProbMul: 1.3,
    trickProbMul: 0.6,
    bossEveryMul: 1.3,
    lastChanceMul: 1.4,
    scoreMul: 0.85,
  },
  normal: {
    id: "normal",
    label: "NORMAL",
    speedMul: 1.0,
    riskWarmupBonus: 0,
    riskProbMul: 1.0,
    scoreProbMul: 1.0,
    trickProbMul: 1.0,
    bossEveryMul: 1.0,
    lastChanceMul: 1.0,
    scoreMul: 1.0,
  },
  hard: {
    id: "hard",
    label: "HARD",
    speedMul: 1.2,
    riskWarmupBonus: -2,
    riskProbMul: 1.3,
    scoreProbMul: 0.7,
    trickProbMul: 1.4,
    bossEveryMul: 0.8,
    lastChanceMul: 0.65,
    scoreMul: 1.25,
  },
};

export const DEFAULT_DIFFICULTY: DifficultyId = "normal";

export function profileById(id: DifficultyId): DifficultyProfile {
  return DIFFICULTY_PROFILES[id] ?? DIFFICULTY_PROFILES.normal;
}
